import { Link } from "react-router-dom";
import { ArrowRight, FileText, Users, ShieldCheck, Lightbulb, Megaphone, TrendingUp } from "lucide-react";

const lifecycle = [
  { icon: FileText, title: "Report", desc: "Document what happened — real incidents and scenarios, submitted directly by the people who lived them." },
  { icon: Users, title: "Connect", desc: "Find support and know you're not facing it alone. Every experience matters, on its own." },
  { icon: ShieldCheck, title: "Reveal Patterns", desc: "We surface patterns across documented incidents — which businesses, industries, and situations show up again and again." },
  { icon: Lightbulb, title: "Build Solutions", desc: "Turn evidence into real solutions — community education, business engagement, and policy proposals." },
  { icon: Megaphone, title: "Act Together", desc: "People of every race who believe in equality contribute their voice, time, expertise, and resources." },
  { icon: TrendingUp, title: "Measure Change", desc: "We keep asking the question that matters: did it work? Did behavior change, did harassment decrease, did a policy shift?" },
];

export default function LifecycleBand() {
  return (
    <section className="bg-zinc-950 text-white py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4FD1C5] mb-3">More Than a Complaint Board</p>
          <h2 className="font-display font-extrabold uppercase text-3xl md:text-4xl tracking-tight mb-4">
            A Movement With a Concrete Goal
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            Documenting bias is step one. The point is turning individual experiences into support, evidence,
            and organized action — then keeping score on whether it actually worked. Racial bias won't end
            through the voices of those experiencing it alone; lasting change happens when people across
            racial lines stand together against it.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {lifecycle.map((p) => (
            <div key={p.title} className="bg-zinc-950 p-8 hover:bg-zinc-900 transition-colors">
              <div className="h-11 w-11 rounded-full border border-[#4FD1C5]/40 flex items-center justify-center mb-5">
                <p.icon className="h-5 w-5 text-[#4FD1C5]" />
              </div>
              <h3 className="font-display font-bold uppercase text-sm tracking-wide mb-2">{p.title}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 bg-zinc-900 border border-white/10 rounded-sm p-6 md:p-8 max-w-3xl mx-auto text-left">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4FD1C5] mb-4 text-center sm:text-left">
            Solutions We're Developing — Proposals in Progress
          </p>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <p className="font-display font-bold text-sm text-white mb-1">Fair Treatment in Public-Facing Roles</p>
              <p className="text-zinc-400 text-xs leading-relaxed">
                Researching and advocating for job-related, evidence-based screening and training for roles
                that interact with the public — including retail and public safety.
              </p>
            </div>
            <div>
              <p className="font-display font-bold text-sm text-white mb-1">Safer 911 Call Handling</p>
              <p className="text-zinc-400 text-xs leading-relaxed">
                Developing proposals for dispatcher training and structured questions that flag potentially
                biased or misleading calls, with accountability after misuse — while keeping response fast
                for real emergencies.
              </p>
            </div>
          </div>
        </div>
        <div className="text-center mt-10">
          <Link to="/about" data-testid="home-learn-more-mission-link" className="inline-flex items-center gap-1 text-sm font-semibold text-[#4FD1C5] hover:underline">
            Read our full mission <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
